import { EnrichmentError } from 'components/common-form/builder/form/enrichment-error';
import { TopBar } from 'components/common-form/builder/form/node/block/TopBar';
import { MainFormSkeleton } from 'components/common-form/components/mainFormSkeleton/MainFormSkeleton';
import { BlockMetadataContext } from 'components/common-form/contexts/BlockMetadataContext';
import { DataApi, DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { QuotedContext } from 'components/common-form/contexts/QuotedContext';
import { SmartQuoteContext } from 'components/common-form/contexts/SmartQuoteContext';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { WithPatchedFieldsContext } from 'components/common-form/contexts/WithPatchedFieldsContext';
import { FormAnalyticsProvider } from 'components/form-analytic-provider';
import type { CrossBrowserState } from 'contexts/crossBrowserContext/CrossBrowserContext';
import { CrossBrowserContext } from 'contexts/crossBrowserContext/CrossBrowserContext';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import type { FormRecord } from 'models/formError';
import type { BlockMetadata } from 'models/metadata/block.definition';
import { FC, useCallback, useMemo } from 'react';
import type { ProductGraph } from 'state/product-description/model';

import './productLayoutsNext';

import './QuoteForm.scss';

import { QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';

import { ProductViewContext } from '../common-form/contexts/ProductViewContext';
import { MainForm } from '../common-form/MainForm';
import { SidePanel } from '../common-form/sidePanel';
import { Summary } from '../common-form/sidePanel/docToolbar/summary';
import { SidePanelContainer } from './sidePanelLayout/SidePanelContainer';
import type { StaticFormProps } from './StaticForm.connect';
import { connectStaticForm } from './StaticForm.connect';

function getGimmickToggleFields(
  quoteWorkspaceVersion: QuoteWorkspaceVersion,
  mustForceAdvancedMode: boolean,
  productDescription?: ProductGraph,
  additionalFeaturesBlockMetadata?: BlockMetadata,
) {
  if (
    quoteWorkspaceVersion === 'Standard' &&
    additionalFeaturesBlockMetadata?.isForAdvancedMode === true &&
    mustForceAdvancedMode === false
  ) {
    return [];
  }
  const clientBlockChildren = productDescription?.find(n => n.id === 'ClientSales')?.children ?? [];
  return clientBlockChildren.find(c => c.id === 'AddonProductFeatures')?.children?.map(x => x.id) ?? [];
}

const PRODUCT_SUBTYPE_FIELD_ID = 'ProductSubtype';
export const StaticForm: FC<StaticFormProps> = ({
  product,
  productView,
  blocksMetadata,
  fieldsMetadata,
  combinedFormData: data,
  formId,
  setFieldValue,
  isLoading,
  canReset,
  canClone,
  canOpenInBatchWorkspace,
  isQuoted,
  onResetTabClicked,
  onRefreshDefaultDatesClicked,
  onRefreshScheduleClicked,
  productDescription,
  gimmicksDescription,
  validationErrors,
  isUserInternal,
  crossBrowserDirty,
  onTabCloned,
  onTabOpenedInBatchWorkspace,
  negotiationId,
  onRollback,
  canReloadSession,
  isNotEditable,
  isTabFrozen,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
  aiGeneratedFields,
}) => {
  const dataApi: DataApi = useMemo(() => ({ setFieldValue }), [setFieldValue]);
  const gimmickToggleFields = useMemo(
    () =>
      gimmicksDescription !== undefined && blocksMetadata !== undefined
        ? getGimmickToggleFields(
            quoteWorkspaceVersion,
            mustForceAdvancedMode,
            productDescription,
            blocksMetadata['AddonProductFeatures'],
          )
        : undefined,
    [productDescription, gimmicksDescription, quoteWorkspaceVersion, blocksMetadata, mustForceAdvancedMode],
  );

  const productSubtype = data[PRODUCT_SUBTYPE_FIELD_ID] as string | undefined;

  const onSessionRollback = useCallback(() => onRollback(negotiationId!), [onRollback, negotiationId]);
  const crossBrowserContext = useMemo<CrossBrowserState>(
    () => ({ isDirty: crossBrowserDirty, isLocked: isTabFrozen }),
    [isTabFrozen, crossBrowserDirty],
  );

  const enrichedFieldMetadata: typeof fieldsMetadata = useMemo(() => {
    const entries = Object.entries(fieldsMetadata).map(([key, value]) => [
      key,
      {
        ...value,
        isAiGenerated: aiGeneratedFields?.includes(key),
      },
    ]);
    return Object.fromEntries(entries);
  }, [fieldsMetadata, aiGeneratedFields]);

  return (
    <CrossBrowserContext.Provider value={crossBrowserContext}>
      <ProductViewContext.Provider value={productView}>
        <QuotedContext.Provider value={isQuoted}>
          <BlockMetadataContext.Provider value={blocksMetadata}>
            <FieldMetadataContext.Provider value={enrichedFieldMetadata}>
              <SmartQuoteContext.Provider value={aiGeneratedFields}>
                <DataContext.Provider value={[data, dataApi]}>
                  <WithPatchedFieldsContext formId={formId}>
                    <WithInvalidFieldsContext<FormRecord, QuoteKeys, QuoteKeysPrefix>
                      validationErrors={validationErrors}
                    >
                      <FormAnalyticsProvider name="advanced form">
                        <EnrichmentError />
                        <SidePanelContainer
                          topBar={
                            !isLoading && (
                              <TopBar
                                gimmickTogglesFieldIds={gimmickToggleFields}
                                isOnBehalfUser={isUserInternal}
                                canReset={canReset}
                                canClone={canClone}
                                canOpenInBatchWorkspace={canOpenInBatchWorkspace}
                                onTabCloned={onTabCloned}
                                onTabOpenedInBatchWorkspace={onTabOpenedInBatchWorkspace}
                                onResetTabClicked={onResetTabClicked}
                                onRefreshDefaultDatesClicked={onRefreshDefaultDatesClicked}
                                onRefreshScheduleClicked={onRefreshScheduleClicked}
                                isNotEditable={isNotEditable}
                                productSubtype={productSubtype}
                              />
                            )
                          }
                          sidePanel={<SidePanel tabId={formId} workspaceType="static" />}
                        >
                          {!isLoading && productDescription ? (
                            <MainForm
                              tabId={formId}
                              isNotEditable={isNotEditable}
                              isUserInternal={isUserInternal}
                              product={product!}
                              productDescription={productDescription}
                              gimmickToggleFields={gimmickToggleFields}
                              gimmicksDescription={gimmicksDescription}
                              crossBrowserDirty={crossBrowserDirty}
                              workspaceType="static"
                              onSessionReload={onSessionRollback}
                              canReloadSession={canReloadSession}
                              quoteWorkspaceVersion={quoteWorkspaceVersion}
                              batchWorkspaceVersion={batchWorkspaceVersion}
                              mustForceAdvancedMode={mustForceAdvancedMode}
                            />
                          ) : (
                            <MainFormSkeleton />
                          )}
                          <Summary
                            tabId={formId}
                            workspaceType="static"
                            quoteWorkspaceVersion={quoteWorkspaceVersion}
                            batchWorkspaceVersion={batchWorkspaceVersion}
                            mustForceAdvancedMode={mustForceAdvancedMode}
                          />
                        </SidePanelContainer>
                      </FormAnalyticsProvider>
                    </WithInvalidFieldsContext>
                  </WithPatchedFieldsContext>
                </DataContext.Provider>
              </SmartQuoteContext.Provider>
            </FieldMetadataContext.Provider>
          </BlockMetadataContext.Provider>
        </QuotedContext.Provider>
      </ProductViewContext.Provider>
    </CrossBrowserContext.Provider>
  );
};
StaticForm.displayName = 'StaticForm';

export const ConnectedStaticForm = connectStaticForm(StaticForm);
ConnectedStaticForm.displayName = 'ConnectedStaticForm';
